// ═══════════════════════════════════════════════════════════════
//  Scenario Analysis
//  • Monte Carlo simulation (runs in-browser)
//  • Best / Base / Worst case comparison table
//  • What-if sliders → live P&L impact
//  • Save & compare multiple scenarios
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Cell,
} from "recharts";
import MonteCarloChart from "../common/MonteCarloChart";
import LoaderComponent from "../../../../libraries/loader/loader";
import { PBExecuteQuery, PBCreateData, Q, userMeta, fmtDate } from "../../../../services/process/PlanningBudgeting/planningBudgetingService";
const SECTION = ({ title, children }) => (
  <div style={{
    background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 14px rgba(73,105,171,0.09)",
    padding: "22px 24px", marginBottom: 22,
  }}>
    <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 16 }}>{title}</div>
    {children}
  </div>
);

const SliderRow = ({ label, value, min, max, step, onChange, unit = "", color = "#4969ab" }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color }}>
        {unit === "₹" ? `₹${Number(value).toLocaleString("en-IN")}` : `${value}${unit}`}
      </span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ width: "100%", accentColor: color }} />
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#bbb", marginTop: 2 }}>
      <span>{unit === "₹" ? `₹${Number(min).toLocaleString("en-IN")}` : `${min}${unit}`}</span>
      <span>{unit === "₹" ? `₹${Number(max).toLocaleString("en-IN")}` : `${max}${unit}`}</span>
    </div>
  </div>
);

const ScenarioCard = ({ label, revenue, expense, profit, color, probability }) => {
  const margin = revenue ? ((profit / revenue) * 100).toFixed(1) : 0;
  return (
    <div style={{
      flex: 1, borderRadius: 12, padding: "18px 20px",
      background: `${color}0d`, border: `2px solid ${color}40`,
      minWidth: 180,
    }}>
      <div style={{ fontSize: 11, color, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
        {label}
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: "#aaa" }}>Revenue</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2340" }}>
          ₹{(revenue / 10000000).toFixed(2)}Cr
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: "#aaa" }}>Expense</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#c0392b" }}>
          ₹{(expense / 10000000).toFixed(2)}Cr
        </div>
      </div>
      <div style={{ paddingTop: 10, borderTop: `1px solid ${color}30` }}>
        <div style={{ fontSize: 10, color: "#aaa" }}>Net Profit</div>
        <div style={{ fontSize: 20, fontWeight: 900, color }}>
          ₹{(profit / 10000000).toFixed(2)}Cr
        </div>
        <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>Margin: {margin}%</div>
      </div>
      {probability !== undefined && (
        <div style={{
          marginTop: 10, background: `${color}20`, borderRadius: 6,
          padding: "4px 8px", fontSize: 11, color, fontWeight: 700,
        }}>
          Probability: {probability}%
        </div>
      )}
    </div>
  );
};

export default function ScenarioAnalysis() {
  const [tab,        setTab]       = useState("whatif");
  const [scenarios,  setScenarios] = useState([]);
  const [loading,    setLoading]   = useState(false);

  // What-if assumptions
  const [baseRev,    setBaseRev]    = useState(50000000);
  const [baseExp,    setBaseExp]    = useState(37500000);
  const [revGrowth,  setRevGrowth]  = useState(10);
  const [expGrowth,  setExpGrowth]  = useState(7);
  const [taxRate,    setTaxRate]    = useState(25);
  const [revStd,     setRevStd]     = useState(15);
  const [expStd,     setExpStd]     = useState(10);
  const [iterations, setIterations] = useState(5000);

  // Save scenario form
  const [scenForm, setScenForm] = useState({
    scenario_name: "", scenario_type: "Revenue", plan_year: new Date().getFullYear(),
    probability_percent: 50, risk_notes: "", status: "Draft",
  });

  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    try {
      const c = PBExecuteQuery();
      const res = await axios.post(c[1], { query: Q.scenarioList(userCredentials.selectedentity) }, c[0]);
      setScenarios(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userCredentials.selectedentity]);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  // ── Live scenario calculations ────────────────────────────────
  const scenarios3 = useMemo(() => {
    const bestRev  = baseRev  * (1 + revGrowth / 100) * 1.15;
    const bestExp  = baseExp  * (1 + expGrowth / 100) * 0.90;
    const baseRevV = baseRev  * (1 + revGrowth / 100);
    const baseExpV = baseExp  * (1 + expGrowth / 100);
    const worstRev = baseRev  * (1 + revGrowth / 100) * 0.80;
    const worstExp = baseExp  * (1 + expGrowth / 100) * 1.15;

    const afterTax = (p) => p * (1 - taxRate / 100);

    return {
      best:  { revenue: bestRev,  expense: bestExp,  profit: afterTax(bestRev  - bestExp),  probability: 25 },
      base:  { revenue: baseRevV, expense: baseExpV, profit: afterTax(baseRevV - baseExpV), probability: 50 },
      worst: { revenue: worstRev, expense: worstExp, profit: afterTax(worstRev - worstExp), probability: 25 },
    };
  }, [baseRev, baseExp, revGrowth, expGrowth, taxRate]);

  // Bar chart data
  const comparisonData = [
    { name: "Revenue", Best: scenarios3.best.revenue, Base: scenarios3.base.revenue, Worst: scenarios3.worst.revenue },
    { name: "Expense", Best: scenarios3.best.expense, Base: scenarios3.base.expense, Worst: scenarios3.worst.expense },
    { name: "Profit",  Best: scenarios3.best.profit,  Base: scenarios3.base.profit,  Worst: scenarios3.worst.profit  },
  ];

  const handleSaveScenario = async () => {
    const payload = {
      database: "planning_budgeting",
      table: "scenario_plan",
      formData: {
        ...scenForm,
        base_value:       scenarios3.base.profit,
        best_case_value:  scenarios3.best.profit,
        worst_case_value: scenarios3.worst.profit,
        ...userMeta(userCredentials),
      },
    };
    try {
      const c = PBCreateData();
      await axios.post(c[1], payload, c[0]);
      alert("Scenario saved successfully!");
      fetchScenarios();
      setTab("saved");
    } catch (e) {
      alert("Error saving scenario");
    }
  };

  if (loading) return <LoaderComponent loaderOpen />;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2340", marginBottom: 4 }}>Scenario Analysis</div>
      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 18 }}>
        What-if modelling, Monte Carlo simulation & scenario comparison
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 0, marginBottom: 22, borderBottom: "2px solid #e8ecf4" }}>
        {[
          ["whatif",  "🎛️ What-If Analysis"],
          ["monte",   "🎲 Monte Carlo"],
          ["saved",   `📁 Saved Scenarios (${scenarios.length})`],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "8px 20px", border: "none", cursor: "pointer",
            background: tab === k ? "#4969ab" : "transparent",
            color: tab === k ? "#fff" : "#666",
            fontWeight: tab === k ? 700 : 400,
            fontSize: 13, borderRadius: "8px 8px 0 0", transition: "all 0.15s",
          }}>{lbl}</button>
        ))}
      </div>

      {/* ══ TAB: What-If ══ */}
      {tab === "whatif" && (
        <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 22 }}>

          {/* Assumption sliders */}
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <SECTION title="🎛️ Assumptions">
              <SliderRow label="Base Revenue (₹)"     value={baseRev}   min={1000000}  max={500000000} step={500000}  onChange={setBaseRev}   unit="₹" color="#4969ab" />
              <SliderRow label="Base Expense (₹)"     value={baseExp}   min={1000000}  max={500000000} step={500000}  onChange={setBaseExp}   unit="₹" color="#c0392b" />
              <SliderRow label="Revenue Growth %"     value={revGrowth} min={-30}      max={100}       step={1}       onChange={setRevGrowth} unit="%" color="#2d8c4e" />
              <SliderRow label="Expense Growth %"     value={expGrowth} min={-20}      max={80}        step={1}       onChange={setExpGrowth} unit="%" color="#e67e22" />
              <SliderRow label="Tax Rate %"           value={taxRate}   min={0}        max={50}        step={0.5}     onChange={setTaxRate}  unit="%" color="#7b68ee" />
            </SECTION>

            {/* Save scenario */}
            <SECTION title="💾 Save This Scenario">
              {[
                { key: "scenario_name", label: "Scenario Name",  type: "text" },
                { key: "plan_year",     label: "Plan Year",       type: "number" },
                { key: "risk_notes",    label: "Risk Notes",      type: "textarea" },
              ].map(({ key, label, type }) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, color: "#888", fontWeight: 600, display: "block", marginBottom: 3 }}>{label}</label>
                  {type === "textarea" ? (
                    <textarea value={scenForm[key]} onChange={(e) => setScenForm((p) => ({ ...p, [key]: e.target.value }))}
                      rows={2} style={{ width: "100%", padding: "6px 10px", borderRadius: 7, border: "1px solid #d0d8ec", fontSize: 12, boxSizing: "border-box" }} />
                  ) : (
                    <input type={type} value={scenForm[key]} onChange={(e) => setScenForm((p) => ({ ...p, [key]: e.target.value }))}
                      style={{ width: "100%", padding: "6px 10px", borderRadius: 7, border: "1px solid #d0d8ec", fontSize: 13, boxSizing: "border-box" }} />
                  )}
                </div>
              ))}
              <button onClick={handleSaveScenario} style={{
                width: "100%", padding: "9px", background: "#4969ab", color: "#fff",
                border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14, marginTop: 4,
              }}>Save Scenario</button>
            </SECTION>
          </div>

          {/* Output */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* 3 scenario cards */}
            <div style={{ display: "flex", gap: 14 }}>
              <ScenarioCard label="Worst Case" {...scenarios3.worst} color="#c0392b" />
              <ScenarioCard label="Base Case"  {...scenarios3.base}  color="#4969ab" />
              <ScenarioCard label="Best Case"  {...scenarios3.best}  color="#2d8c4e" />
            </div>

            {/* Comparison bar chart */}
            <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 14px rgba(73,105,171,0.09)", padding: "18px 20px" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 14 }}>Scenario Comparison</div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={comparisonData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `₹${(v/10000000).toFixed(1)}Cr`} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                  <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Worst" fill="#c0392b" radius={[4,4,0,0]} opacity={0.85} />
                  <Bar dataKey="Base"  fill="#4969ab" radius={[4,4,0,0]} />
                  <Bar dataKey="Best"  fill="#2d8c4e" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Sensitivity table */}
            <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 14px rgba(73,105,171,0.09)", padding: "18px 20px" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 12 }}>Sensitivity — Revenue Growth Impact on Profit</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f5f7fb" }}>
                    {["Rev Growth", "-10%", "0%", "+10%", "+20%", "+30%"].map((h) => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: h === "Rev Growth" ? "left" : "right", color: "#888", fontWeight: 600, fontSize: 11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[-10, 0, 10, 20, 30].map((eg) => (
                    <tr key={eg} style={{ borderBottom: "1px solid #f0f2f8" }}>
                      <td style={{ padding: "8px 10px", fontWeight: 600, color: "#555" }}>Exp +{eg}%</td>
                      {[-10, 0, 10, 20, 30].map((rg) => {
                        const p = (baseRev * (1 + rg/100)) - (baseExp * (1 + eg/100));
                        const pt = p * (1 - taxRate/100);
                        const isCurrent = rg === revGrowth && eg === expGrowth;
                        return (
                          <td key={rg} style={{
                            padding: "8px 10px", textAlign: "right", fontWeight: isCurrent ? 800 : 400,
                            color: pt > 0 ? "#2d8c4e" : "#c0392b",
                            background: isCurrent ? "#e8f0ff" : "transparent",
                          }}>
                            ₹{(pt/10000000).toFixed(2)}Cr
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ TAB: Monte Carlo ══ */}
      {tab === "monte" && (
        <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 22 }}>
          <SECTION title="⚙️ Simulation Parameters">
            <SliderRow label="Base Revenue (₹)"      value={baseRev}    min={1000000}  max={500000000} step={500000} onChange={setBaseRev}    unit="₹" color="#4969ab" />
            <SliderRow label="Base Expense (₹)"      value={baseExp}    min={1000000}  max={500000000} step={500000} onChange={setBaseExp}    unit="₹" color="#c0392b" />
            <SliderRow label="Revenue Volatility σ%" value={revStd}     min={1}        max={50}        step={1}      onChange={setRevStd}     unit="%" color="#7b68ee" />
            <SliderRow label="Expense Volatility σ%" value={expStd}     min={1}        max={50}        step={1}      onChange={setExpStd}     unit="%" color="#e67e22" />
            <SliderRow label="Iterations"            value={iterations} min={500}      max={20000}     step={500}    onChange={setIterations} unit="" color="#2d8c4e" />
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 8, lineHeight: 1.6 }}>
              Uses Box-Muller transform to generate normally-distributed random variables.
              Higher volatility = wider distribution = more uncertainty.
            </div>
          </SECTION>
          <MonteCarloChart
            baseRevenue={baseRev}
            baseExpense={baseExp}
            revenueStdPct={revStd}
            expenseStdPct={expStd}
            iterations={iterations}
            height={400}
            title="Monte Carlo Profit Distribution"
          />
        </div>
      )}

      {/* ══ TAB: Saved Scenarios ══ */}
      {tab === "saved" && (
        <div>
          {scenarios.length === 0 ? (
            <div style={{ textAlign: "center", color: "#bbb", padding: 60, fontSize: 14 }}>
              No saved scenarios yet. Build one in the What-If Analysis tab.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
              {scenarios.map((s) => (
                <div key={s.id} style={{
                  background: "#fff", borderRadius: 12,
                  boxShadow: "0 2px 10px rgba(73,105,171,0.09)",
                  padding: "16px 20px",
                  borderLeft: `4px solid ${
                    s.status === "Approved" ? "#2d8c4e" :
                    s.status === "Rejected" ? "#c0392b" : "#4969ab"
                  }`,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 8 }}>{s.scenario_name}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                    {[
                      { l: "Base",  v: s.base_value,       c: "#4969ab" },
                      { l: "Best",  v: s.best_case_value,  c: "#2d8c4e" },
                      { l: "Worst", v: s.worst_case_value, c: "#c0392b" },
                    ].map(({ l, v, c }) => (
                      <div key={l} style={{ background: `${c}10`, borderRadius: 8, padding: "6px 8px" }}>
                        <div style={{ fontSize: 9, color: c, fontWeight: 700, textTransform: "uppercase" }}>{l}</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#1a2340" }}>
                          ₹{(Number(v)/10000000).toFixed(2)}Cr
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    Type: {s.scenario_type} · Year: {s.plan_year} · P: {s.probability_percent}%
                  </div>
                  {s.risk_notes && (
                    <div style={{ fontSize: 11, color: "#888", marginTop: 6, fontStyle: "italic" }}>"{s.risk_notes}"</div>
                  )}
                  <div style={{
                    marginTop: 8, fontSize: 10, fontWeight: 700, padding: "3px 10px",
                    borderRadius: 12, display: "inline-block",
                    background: s.status === "Approved" ? "#e8f5ee" : "#f0f2f8",
                    color:      s.status === "Approved" ? "#2d8c4e" : "#888",
                  }}>{s.status}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
